// ============================================================================
// JARVISTRAVEL - BACKEND UNAVAILABLE BANNER (JAR-314)
// A slim, app-wide status strip shown while the backend can't be reached
// (network down, timeout, or 5xx). Amber as a state ACCENT on a neutral
// surface — not error-red — because the state is transient and the user's
// data is safe. Auto-dismisses the moment any request succeeds; "Try again"
// re-probes reachability. Mounted once at the app root (works on auth screens
// too, which have no frame of their own).
// ============================================================================

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { CloudOff, Loader2 } from 'lucide-react';
import { getBackendStatus, subscribeBackendStatus } from '@/app/api/backendStatus';
import { probeBackend } from '@/app/api/client';

// How often (ms) to re-probe reachability while the banner is visible, so the
// app auto-recovers when the backend comes back (JAR-314 / H1 fix).
const PROBE_INTERVAL_MS = 30_000;

export function BackendUnavailableBanner() {
  const status = useSyncExternalStore(subscribeBackendStatus, getBackendStatus, getBackendStatus);
  const [checking, setChecking] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Periodic auto-probe while offline — clears on recovery or unmount so we
  // never probe when the backend is known-reachable (H1 / JAR-314).
  useEffect(() => {
    if (status === 'offline') {
      intervalRef.current = setInterval(() => { void probeBackend(); }, PROBE_INTERVAL_MS);
    }
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [status]);

  if (status === 'online') return null;

  const retry = async () => {
    if (checking) return;
    setChecking(true);
    try {
      await probeBackend(); // success flips the store → this banner unmounts
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      // In the normal flow at the app root, so it pushes the page (header and
      // nav included) down instead of covering it, and scrolls off as the page
      // scrolls (JAR-2497). First of the two app-root strips, so it sits above
      // the update strip when both show.
      className="bg-surface border-b border-warning/40 shadow-sm animate-reveal motion-reduce:animate-none"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="mx-auto w-full max-w-[440px] md:max-w-[960px] flex items-center gap-2.5 px-4 py-2.5">
        <CloudOff className="w-4 h-4 shrink-0 text-warning" strokeWidth={2} aria-hidden="true" />
        <p className="flex-1 min-w-0 text-sm leading-tight">
          <span className="font-semibold text-ink">Can’t reach our servers.</span>{' '}
          <span className="text-body">Your data is safe. Try again in a moment.</span>
        </p>
        <button
          onClick={retry}
          disabled={checking}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-warning/15 px-3 py-1.5 text-sm font-semibold text-warning hover:bg-warning/25 disabled:opacity-60 transition-colors"
        >
          {checking && <Loader2 className="w-3.5 h-3.5 animate-spin motion-reduce:animate-none" strokeWidth={2.5} aria-hidden="true" />}
          {checking ? 'Checking…' : 'Try again'}
        </button>
      </div>
    </div>
  );
}

export default BackendUnavailableBanner;
